import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ClipboardList, Dumbbell, Brain, BarChart3, Trophy, Calendar, Gamepad2,
  Heart, FileText, FlaskConical, BookOpen, Users, Zap, Gift, Crown,
  MessageCircle, TrendingUp, User as UserIcon, Target, ArrowLeft, Sparkles,
  Menu, Moon, Sun, Globe, LogOut, Info, Share2
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { clearToken } from "../App";
import { useTheme } from "../hooks/useTheme";

const featureCategories = [
  {
    label: "Daily Routine",
    items: [
      { icon: ClipboardList, label: "Daily Check-in", path: "/questionnaire", desc: "Assess your state" },
      { icon: Dumbbell, label: "Training Plan", path: "/training", desc: "AI-generated workouts" },
      { icon: BarChart3, label: "Progress", path: "/progress", desc: "Track your journey" },
      { icon: Calendar, label: "Weekly Calendar", path: "/calendar", desc: "Plan your week" },
    ],
  },
  {
    label: "Brain & Body",
    items: [
      { icon: Gamepad2, label: "Brain Games", path: "/games", desc: "Cognitive training", pro: true },
      { icon: FlaskConical, label: "Workout Lab", path: "/workout-lab", desc: "Custom exercises", pro: true },
      { icon: Heart, label: "Meditation", path: "/mindfulness", desc: "AI guided sessions", pro: true },
      { icon: BookOpen, label: "Deep Dive", path: "/deep-dive", desc: "Clinical assessments", pro: true },
    ],
  },
  {
    label: "Community & Growth",
    items: [
      { icon: Trophy, label: "Achievements", path: "/achievements", desc: "Badges & streaks" },
      { icon: Users, label: "Activity Feed", path: "/social-feed", desc: "Community updates" },
      { icon: Zap, label: "Challenges", path: "/seasonal-challenges", desc: "Seasonal events" },
      { icon: MessageCircle, label: "Messages", path: "/messages", desc: "User messaging" },
    ],
  },
  {
    label: "More",
    items: [
      { icon: FileText, label: "Weekly Report", path: "/report", desc: "Performance review", pro: true },
      { icon: TrendingUp, label: "Digest", path: "/digest", desc: "Weekly insights", pro: true },
      { icon: UserIcon, label: "Profile Card", path: "/profile-card", desc: "Shareable card" },
      { icon: Gift, label: "Referral", path: "/referral", desc: "Invite friends" },
      { icon: Crown, label: "Subscription", path: "/subscription", desc: "Manage plan" },
      { icon: BookOpen, label: "Science Blog", path: "/blog", desc: "Latest research" },
    ],
  },
];

export const FeatureMenuDropdown = () => {
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-sky-600 hover:text-sky-800 hover:bg-sky-100"
          data-testid="feature-menu-btn"
          title="Features"
        >
          <Menu className="w-5 h-5" strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-72 max-h-[70vh] overflow-y-auto"
        data-testid="feature-menu-dropdown"
      >
        {featureCategories.map((cat, ci) => (
          <div key={cat.label}>
            {ci > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs text-sky-500 font-medium">
              {cat.label}
            </DropdownMenuLabel>
            {cat.items.map((item) => {
              const Icon = item.icon;
              return (
                <DropdownMenuItem
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className="cursor-pointer flex items-center gap-3 py-2"
                  data-testid={`feature-menu-item-${item.path.slice(1)}`}
                >
                  <Icon className="w-4 h-4 text-sky-500 flex-shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-sky-800">{item.label}</span>
                    {item.pro && (
                      <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 font-medium">
                        PRO
                      </span>
                    )}
                  </div>
                </DropdownMenuItem>
              );
            })}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const FeatureMenuPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isDark, toggle: toggleDarkMode } = useTheme();

  const handleLogout = async () => {
    try { const axios = (await import("axios")).default; const { API } = await import("../App"); await axios.post(`${API}/auth/logout`); } catch {} 
    clearToken();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-sky-50 dark:bg-slate-900" data-testid="feature-menu-page">
      <div className="fixed top-0 right-0 w-96 h-96 bg-sky-300/20 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-1.5 text-sky-500 hover:text-sky-700 transition-colors"
            data-testid="menu-back-btn"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-indigo-400 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-lg font-light text-sky-900 dark:text-sky-100" style={{ fontFamily: "Outfit" }}>
              {t("common.appName")}
            </span>
          </div>
        </div>
      </header>

      <main className="pt-20 pb-12 px-4">
        <div className="max-w-lg mx-auto">
          <h1 className="text-2xl font-light text-sky-900 dark:text-sky-100 mb-6" style={{ fontFamily: "Outfit" }}>
            All Features
          </h1>

          <div className="space-y-6">
            {featureCategories.map((cat) => (
              <div key={cat.label}>
                <h2 className="text-xs font-semibold text-sky-500 uppercase tracking-wider mb-3 px-1">
                  {cat.label}
                </h2>
                <div className="grid grid-cols-1 gap-2">
                  {cat.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        className="glass-card p-4 flex items-center gap-4 hover:shadow-md transition-all text-left w-full group"
                        data-testid={`menu-item-${item.path.slice(1)}`}
                      >
                        <div className="w-10 h-10 rounded-xl bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center group-hover:bg-sky-200 dark:group-hover:bg-sky-800/40 transition-colors">
                          <Icon className="w-5 h-5 text-sky-600 dark:text-sky-400" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-sky-800 dark:text-sky-200">{item.label}</span>
                            {item.pro && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 font-medium">
                                PRO
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{item.desc}</p>
                        </div>
                        <svg className="w-4 h-4 text-slate-300 group-hover:text-sky-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Quick Actions — items hidden from mobile header */}
            <div>
              <h2 className="text-xs font-semibold text-sky-500 uppercase tracking-wider mb-3 px-1">
                Settings & Account
              </h2>
              <div className="grid grid-cols-1 gap-2">
                <button onClick={() => navigate('/profile-card')} className="glass-card p-4 flex items-center gap-4 hover:shadow-md transition-all text-left w-full group" data-testid="menu-item-profile-card">
                  <div className="w-10 h-10 rounded-xl bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center"><Share2 className="w-5 h-5 text-sky-600 dark:text-sky-400" strokeWidth={1.5} /></div>
                  <div className="flex-1"><span className="text-sm font-medium text-sky-800 dark:text-sky-200">Share Profile Card</span><p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Your shareable achievement card</p></div>
                </button>
                <button onClick={toggleDarkMode} className="glass-card p-4 flex items-center gap-4 hover:shadow-md transition-all text-left w-full group" data-testid="menu-item-dark-mode">
                  <div className="w-10 h-10 rounded-xl bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center">{isDark ? <Sun className="w-5 h-5 text-amber-500" strokeWidth={1.5} /> : <Moon className="w-5 h-5 text-sky-600" strokeWidth={1.5} />}</div>
                  <div className="flex-1"><span className="text-sm font-medium text-sky-800 dark:text-sky-200">{isDark ? "Light Mode" : "Dark Mode"}</span><p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Toggle appearance</p></div>
                </button>
                <button onClick={() => navigate('/settings')} className="glass-card p-4 flex items-center gap-4 hover:shadow-md transition-all text-left w-full group" data-testid="menu-item-language">
                  <div className="w-10 h-10 rounded-xl bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center"><Globe className="w-5 h-5 text-sky-600 dark:text-sky-400" strokeWidth={1.5} /></div>
                  <div className="flex-1"><span className="text-sm font-medium text-sky-800 dark:text-sky-200">Language ({(i18n.language || "en").split("-")[0].toUpperCase()})</span><p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Change in Settings</p></div>
                </button>
                <button onClick={() => navigate('/about')} className="glass-card p-4 flex items-center gap-4 hover:shadow-md transition-all text-left w-full group" data-testid="menu-item-about">
                  <div className="w-10 h-10 rounded-xl bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center"><Info className="w-5 h-5 text-sky-600 dark:text-sky-400" strokeWidth={1.5} /></div>
                  <div className="flex-1"><span className="text-sm font-medium text-sky-800 dark:text-sky-200">About LumiFit</span><p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Our story</p></div>
                </button>
                <button onClick={handleLogout} className="glass-card p-4 flex items-center gap-4 hover:shadow-md transition-all text-left w-full group border border-red-100 dark:border-red-900/30" data-testid="menu-item-logout">
                  <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center"><LogOut className="w-5 h-5 text-red-500" strokeWidth={1.5} /></div>
                  <div className="flex-1"><span className="text-sm font-medium text-red-600 dark:text-red-400">Log Out</span><p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Sign out of your account</p></div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FeatureMenuPage;
